/* eslint-disable react/jsx-no-bind */
import {
  Banner,
  Button,
  Card,
  DisplayText,
  FooterHelp,
  Image,
  Layout,
  Link,
  Spinner,
  Stack,
  Subheading,
  TextContainer,
  TextStyle,
} from '@shopify/polaris';
import {connect} from 'react-redux';
import * as PropTypes from 'prop-types';
import React, {Component} from 'react';

import {disableTestMode, isTestMode} from './helpers/test_mode';
import {submitServiceSelection} from './helpers/step';
import {LEGACY_SERVICE_HANDLE_MAP} from './helpers/services';
import OrganizationCard from './organizationCard';
import {setServiceHandle} from '../../actions/service';
import {
  setBreadcrumbs,
  setActiveTaxonomyNode,
} from '../../actions/service-selector';
import {ServiceSelector} from './service-selector';
import sortBy from 'lodash/sortBy';
import {MobilePadding} from 'components/polaris/react/mobile-padding';
import Header from './header';
import railsData from 'utilities/rails-data';

class ServiceSelectionFlow extends Component {
  onServiceSelected = (serviceHandle, activeTaxonomyNode, breadcrumbs) => {
    const handle = LEGACY_SERVICE_HANDLE_MAP[serviceHandle] || serviceHandle;

    this.props.setActiveTaxonomyNode(activeTaxonomyNode);
    this.props.setBreadcrumbs(breadcrumbs);
    this.props.setServiceHandle(handle);
    submitServiceSelection(handle);
  };

  render() {
    return (
      <Layout>
        <Layout.Section>{this.renderNavigation()}</Layout.Section>
        <Layout.Section>
          {this.renderTestModeBanner()}
          <div className="type-container layout__single-col--header">
            {this.renderHeader()}
          </div>
          {this.renderOrganizationCard()}
          <Layout.Section>
            <MobilePadding>
              <TextContainer>
                <Subheading>Choose a service to get started</Subheading>
              </TextContainer>
            </MobilePadding>
          </Layout.Section>
          <Layout.Section>{this.renderTaxonomy()}</Layout.Section>
          <Layout.Section key="footer-help">
            {this.renderFooterHelp()}
          </Layout.Section>
        </Layout.Section>
      </Layout>
    );
  }

  renderNavigation() {
    const totalBriefCount = railsData('total_brief_count');

    return totalBriefCount ? (
      <div className="sm-breadcrumb--button">
        <Button
          plain
          monochrome
          url="/services_marketplace/job_requests?redirect=false"
          icon="chevronLeft"
          accessibilityLabel="All job requests"
        >
          Jobs
        </Button>
      </div>
    ) : null;
  }

  renderTaxonomy() {
    if (this.props.fetchingTaxonomy) {
      return (
        <Stack distribution="center">
          <Spinner />
        </Stack>
      );
    } else {
      return this.renderServiceSelector();
    }
  }

  renderServiceSelector() {
    const taxonomy = this.props.services.reduce((services, item) => {
      return services.concat({
        service: item.service,
        children: sortBy(item.children, ['children']),
      });
    }, []);

    return (
      <Card>
        <ServiceSelector
          taxonomy={taxonomy}
          activeTaxonomyNode={this.props.activeTaxonomyNode || taxonomy}
          breadcrumbs={this.props.breadcrumbs}
          onServiceSelect={this.onServiceSelected}
        />
      </Card>
    );
  }

  renderOrganizationCard() {
    return this.props.organization ? (
      <Layout.Section key="organizationCard">
        <OrganizationCard />
      </Layout.Section>
    ) : null;
  }

  renderFooterHelp() {
    return (
      <FooterHelp>
        Learn more about
        <Link
          url={
            'https://help.shopify.com/en/manual/community/experts/experts-faq'
          }
          external
        >
          {' working with an expert'}
        </Link>
      </FooterHelp>
    );
  }

  renderTestModeBanner() {
    if (!isTestMode()) {
      return null;
    }

    return (
      <Layout.Section key="test-mode-banner">
        <Banner
          title="Test mode"
          status="warning"
          action={{
            content: 'Disable test mode',
            onClick: disableTestMode,
          }}
        >
          <p>
            You are currently in test mode, your brief will not actually contact
            a real partner. If you need to submit an actual job request you can
            disable test mode
          </p>
        </Banner>
      </Layout.Section>
    );
  }

  renderHeader() {
    return this.props.organization ? (
      <Header />
    ) : (
      <MobilePadding>
        <div className="service-selection-header">
          <div className="service-selection-header__content">
            <TextContainer>
              <DisplayText size="large">Hire a Shopify expert</DisplayText>
              <DisplayText size="small">
                <TextStyle variation="subdued">
                  Describe the work you want done and get matched with trusted,
                  independent experts who can help.
                </TextStyle>
              </DisplayText>
            </TextContainer>
          </div>
          <div className="service-selection-header__image-container">
            <Image
              role="presentation"
              alt="Services Marketplace Select Service"
              source="/images/services_marketplace_illustration.svg"
            />
          </div>
        </div>
      </MobilePadding>
    );
  }
}

const TaxonomyPropType = PropTypes.shape({
  service: PropTypes.shape({
    handle: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    shortLabel: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    startingBudget: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  children: PropTypes.array,
});

ServiceSelectionFlow.propTypes = {
  setServiceHandle: PropTypes.func.isRequired,
  services: PropTypes.arrayOf(TaxonomyPropType).isRequired,
  activeTaxonomyNode: PropTypes.arrayOf(TaxonomyPropType),
  breadcrumbs: PropTypes.arrayOf(TaxonomyPropType),
  organization: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    services: state.context.services,
    organization: state.context.organization,
    activeTaxonomyNode: state.context.activeTaxonomyNode,
    breadcrumbs: state.context.breadcrumbs,
    fetchingTaxonomy: state.progress.fetchingTaxonomy,
  };
}

const mapDispatchToProps = {
  setServiceHandle,
  setActiveTaxonomyNode,
  setBreadcrumbs,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ServiceSelectionFlow);
